import { useCallback, useEffect, useState } from "react"
import { useAppDispatch, useAppSelector } from "../redux/hooks"
import { useAccount, useNetwork, useSwitchNetwork } from "wagmi"
import { createPublicClient, createWalletClient, custom, http, parseUnits } from "viem"
import inscriptionABI from "data/abi"
import { setWalletWinOpened } from "../redux/slices/walletSlice"
import { setCurInscription, setResetInfo } from "../redux/slices/coreInfoSlice"
import toast from "react-hot-toast"
import useBrc20Utils from "./useBrc20Utils"
import { useUnisatWallet } from "hooks/wallet/useUnisatWalletProvider"
import { useOkxBitcoinWallet } from "hooks/wallet/useOkxBitcoinWalletProvider"
import { bsc } from "viem/chains"
import { getStore, setOrderStore } from "utils/store"
import { getFeeRate, postSubmitOrder } from "services/brc20Services"
import { setSwapLoading, setSwapStatus } from "hooks/redux/slices/swapSlice"
import { setRefreshData } from "hooks/redux/slices/appSlice"

// btnStatus -1 未连接 -2 交易参数不完整 -3 余额不足 -4 交易资产相同 -5 最小数量 -7 链不同 -31 为选择铭文 -32 请输入正确的EVM地址 -33 请输入正确的BTC地址
const useMainBtnBrc20 = () => {
    const dispatch = useAppDispatch()
    const { address: addressEvm, connector } = useAccount()
    const { isConnected } = useAppSelector((state) => state.wallet)
    const { refreshData } = useAppSelector((state) => state.app)
    const { fromToken, fromChain, toChain, toToken, fromAmount, balanceEnough, isMinUsdt, toAddress, curInscription, minUsdt } = useAppSelector((state) => state.coreInfo)
    const [btnStatus, setBtnStatus] = useState(-1)
    const [btnText, setBtnText] = useState('Swap now')
    const [btnActive, setBtnActive] = useState(true)
    const { switchNetworkAsync } = useSwitchNetwork()
    const { chain: curChain } = useNetwork()
    const { isBTC } = useBrc20Utils()

    const { getProvider: getProviderByUnisat, getPublicKey, address: addressByUnisat } = useUnisatWallet()
    const { getProvider: getProviderByOkx, address: addressByOkx, publicKey} = useOkxBitcoinWallet()

    const checkBtnStatus = useCallback(async () => {
        if(!isConnected) {
            setBtnStatus(-1)
            return
        }

        if(fromChain?.nickName === toChain?.nickName && fromToken?.symbol === toToken?.symbol) {
            setBtnStatus(-4)
            return
        }

        if(isBTC) {
            // 为选择可供交换的铭文
            const objKeys = Object.keys(curInscription)
            if(!objKeys.length)  {
                setBtnStatus(-31)
                return
            }

            if(!(/0[xX][0-9a-fA-F]{40}/.test(toAddress))) {
                setBtnStatus(-32)
                return;
            }
        } else {
            // evm 体系
            if(!(fromChain && toChain && fromToken && toToken && fromAmount)) {
                setBtnStatus(-2)
                return
            }
            // 余额不足
            if(!balanceEnough) {
                setBtnStatus(-3)
                return
            }

            if(curChain?.id !== +fromChain?.network) {
                setBtnStatus(-7)
                return
            }

            if(!(/^(bc1)+[02-9ac-hj-np-z]{39,59}$/.test(toAddress))) {
                setBtnStatus(-33)
                return;
            }
        }

        if(isMinUsdt) {
            setBtnStatus(-5)
            return
        }

        setBtnStatus(1)
    }, [isConnected, fromChain, toChain, fromToken, toToken, isBTC, isMinUsdt, curInscription, toAddress, fromAmount, balanceEnough, curChain?.id])

    useEffect(() => {
        checkBtnStatus()
    }, [checkBtnStatus])

    useEffect(() => {
        switch(btnStatus) {
            case -1:
                setBtnText('Connect your wallet')
                break;
            case -2:
                setBtnText('Input an amount to swap')
                break;
            case -3:
                setBtnText('Your balance is not enough')
                break;
            case -4:
                setBtnText('You can’t swap identical assets')
                break;
            case -5:
                setBtnText(`Swap amount must exceed ${minUsdt} ${toToken?.symbol}`)
                break;
            case -6:
                setBtnText('Swap now')
                break;
            case -7:
                setBtnText(`Switch chain to ${fromChain?.fullName}`)
                break;
            case -31:
                setBtnText(`Please select an inscription`)
                break
            case -32:
                setBtnText(`Enter the correct EVM address`)
                break
            case -33:
                setBtnText(`Enter the correct Bitcoin address`)
                break
            default:
                setBtnText(`Swap now`)
        }
        if([-2, -3, -4, -5, -31, -32, -33].includes(btnStatus)) {
            setBtnActive(false)
        } else {
            setBtnActive(true)
        }
    }, [btnStatus, fromChain?.fullName, minUsdt, toToken?.symbol])

    // 签名 evm
    const signMessageByEvm = useCallback(async (raw: string, walletClient: any) => {
        // raw = keccak256(toHex(raw))
        let signature = await walletClient.signMessage({ 
            account: addressEvm,
            message: raw,
        })
        signature = signature.substring(2)
        return {
            signature,
            publicKey: ''
        }
    }, [addressEvm])

    // 签名 brc20 
    const signMessageByOkx = useCallback(async (raw: string) => {
        const provider = getProviderByOkx()
        // raw = keccak256(toHex(raw))
        const signatureRaw = await provider.signMessage(raw, {
            from: addressByOkx
        })
        const signature = Buffer.from(signatureRaw, 'base64').toString('hex')
        return {
            signature,
            publicKey
        }
    }, [addressByOkx, publicKey, getProviderByOkx])

    const signMessageByUnisat = useCallback(async (raw: string) => {
        const publicKey = await getPublicKey()
        const provider = getProviderByUnisat()
        // raw = keccak256(toHex(raw))
        const signatureRaw = await provider.signMessage(raw)
        const signature = Buffer.from(signatureRaw, 'base64').toString('hex')
        return {
            signature,
            publicKey
        }
    }, [getProviderByUnisat, getPublicKey])

    const fnSubmitOrde = useCallback(async (certHash: string, amount: string, fromAddress: string, publicKey: string, signature: string) =>{
        try {
            if(fromToken && toChain) {
                const params = {
                    certHash: certHash,
                    symbol: fromToken.symbol,
                    amount: amount,
                    toChain: toChain.nickName,
                    toAddr: toAddress
                }
                const headers = {
                    address: fromAddress,
                    publicKey: publicKey,
                    signature: signature
                }

                // 缓存信息
                const originParams = {
                    fromChain,
                    fromToken,
                    toChain,
                    toToken,
                    fromAmount: amount,
                    toAmount: amount,
                    hash: certHash,
                    createTime: new Date().getTime(),
                    submit: true,
                    status: 'successful',
                    id: certHash,
                    isBrc20: true,
                    submitInfo: {
                        params,
                        headers: headers
                    }
                }
                setOrderStore(originParams)

                await postSubmitOrder(params, headers)
            }
        } catch(error) {
            throw error
        }
    }, [fromChain, fromToken, toAddress, toChain, toToken])

    // ERC20币跨链
    const transferByEvm = useCallback(async () => {
        try {
            const provider = await connector?.getProvider()
            const fromAddress = addressEvm  as `0x${string}`
            const contractAddress = fromToken?.contractAddress as `0x${string}`
            const amount = parseUnits(fromAmount, fromToken?.decimals).toString()
            const minterAddress = '0x5300D56450785ce01da8009FdED94a7f8663aA72'

            const publicClient = createPublicClient({
                chain: bsc,
                transport: http()
            }) as any

            // 合约准备
            const walletClient = createWalletClient({
                account: fromAddress,
                chain: bsc,
                transport: custom(provider)
            }) as any

            // 准备参数
            let certHash = ''
            try {
                let gasPrice = await publicClient?.getGasPrice()
                gasPrice = gasPrice*BigInt(6)/BigInt(5)
                const gasLimit = await publicClient?.estimateContractGas({
                    address: contractAddress,
                    abi: inscriptionABI,
                    functionName: 'transfer',
                    account: fromAddress,
                    gasPrice: gasPrice,
                    args: [minterAddress, amount]
                })

                certHash = await walletClient?.writeContract({
                    address: contractAddress,
                    abi: inscriptionABI,
                    functionName: 'transfer',
                    account: fromAddress,
                    chain: bsc,
                    gasPrice: gasPrice,
                    gas: gasLimit,
                    args: [minterAddress, amount]
                })
                dispatch(setSwapStatus(1))
                const txInfo = await publicClient?.waitForTransactionReceipt(
                    { 
                        hash: certHash,
                    }
                )
                if(txInfo.status.toLowerCase() !== 'success') {
                    dispatch(setSwapStatus(99))
                    return false
                }
            } catch(error) {
                dispatch(setSwapStatus(99))
                throw error
            }
            if(!certHash) {
                dispatch(setSwapStatus(99))
                return
            }

            const toSymbol = toToken?.symbol
            const toChainName = toChain.nickName
            const raw = `amount=${fromAmount}&certHash=${certHash}&symbol=${toSymbol}&toAddr=${toAddress}&toChain=${toChainName}`
            
            let  signatureInfo = {
                publicKey: '',
                signature: ''
            }
            try {
                signatureInfo = await signMessageByEvm(raw, walletClient)
            } catch(error) {
                dispatch(setSwapStatus(100))
                throw error
            }
            if(!signatureInfo.signature) {
                dispatch(setSwapStatus(100))
                return
            }
            await fnSubmitOrde(certHash, fromAmount, fromAddress, signatureInfo.publicKey, signatureInfo.signature)
            dispatch(setSwapStatus(2))
        } catch(e) {
            throw e
        } finally {

        }
    }, [connector, addressEvm, fromToken?.contractAddress, fromToken?.decimals, fromAmount, toToken?.symbol, toChain?.nickName, toAddress, fnSubmitOrde, dispatch, signMessageByEvm])

    // BRC20币跨链
    const transferByUnisat = useCallback(async () => {
       try {
            if(toChain && toToken && toAddress && curInscription) {
                const feeRate = await getFeeRate()
                const provider = getProviderByUnisat()
                const inscriptionId = curInscription?.inscriptionId
                const amount = (curInscription?.data as any).amt
                const toSymbol = toToken?.symbol
                const toChainName = toChain?.nickName
                // 准备参数
                let certHash = ''
                try {
                    certHash = await provider.sendInscription("bc1pfdzqnj8t6yh0tudfq9q0zfa0v5t58u98ac8u3dz3sd40e8rslu2sm42xp4", inscriptionId, {feeRate: feeRate })
                } catch(error) {
                    dispatch(setSwapStatus(99))
                    throw error
                }
                if(!certHash) {
                    dispatch(setSwapStatus(99))
                    return
                }
                const raw = `amount=${amount}&certHash=${certHash}&symbol=${toSymbol}&toAddr=${toAddress}&toChain=${toChainName}`
                let  signatureInfo = {
                    publicKey: '',
                    signature: ''
                }
                try {
                    signatureInfo = await signMessageByUnisat(raw)
                } catch(error) {
                    dispatch(setSwapStatus(100))
                    throw error
                }
                if(!signatureInfo.signature) {
                    dispatch(setSwapStatus(100))
                    return
                }
                // 提交接口
                await fnSubmitOrde(certHash, amount, addressByUnisat, signatureInfo.publicKey, signatureInfo.signature)
                dispatch(setSwapStatus(2))
            }
       } catch(error) {

       }
    }, [toChain, toToken, toAddress, curInscription, getProviderByUnisat, fnSubmitOrde, addressByUnisat, dispatch, signMessageByUnisat])

    const transferByOkx = useCallback(async () => {
        if(toChain && toToken && toAddress && curInscription) {
            const provider = getProviderByOkx()
            const inscriptionId = curInscription?.inscriptionId
            const amount = (curInscription?.data as any).amt
            const toSymbol = toToken?.symbol
            const toChainName = toChain?.nickName
            let certHash = ''
            try {
                const { txhash = '' } = await provider.transferNft({ from: addressByOkx, to: "bc1pfdzqnj8t6yh0tudfq9q0zfa0v5t58u98ac8u3dz3sd40e8rslu2sm42xp4", data: inscriptionId, type: 55 })
                certHash = txhash
            } catch(error) {
                dispatch(setSwapStatus(99))
                throw error
            }
            if(!certHash) {
                dispatch(setSwapStatus(99))
                return
            }
            const raw = `amount=${amount}&certHash=${certHash}&symbol=${toSymbol}&toAddr=${toAddress}&toChain=${toChainName}`
            let  signatureInfo = {
                publicKey: '',
                signature: ''
            }
            try {
                signatureInfo = await signMessageByOkx(raw)
            } catch(error) {
                dispatch(setSwapStatus(100))
                throw error
            }
            if(!signatureInfo.signature) {
                dispatch(setSwapStatus(100))
                return
            }
            // 提交接口
            await fnSubmitOrde(certHash, amount, addressByOkx, signatureInfo.publicKey, signatureInfo.signature)
            dispatch(setSwapStatus(2))
        }
    }, [toChain, toToken, toAddress, curInscription, getProviderByOkx, fnSubmitOrde, addressByOkx, dispatch, signMessageByOkx])

    // aggregate
    const btnHandler = useCallback(async () => {
        if(!(fromToken && fromChain)) return
        if(!btnActive) return
        if(btnStatus === -1) {
            // 链接网络
            dispatch(setWalletWinOpened(true))
            return
        }
        if( btnStatus === -7) {
            // 切换网络
            try {
                await switchNetworkAsync?.(+fromChain?.network)
            } catch(error) {

            } finally {
                return
            }
        }
        try {
            dispatch(setSwapLoading(true))
            dispatch(setSwapStatus(0))
            if(isBTC) {
                const walletName = getStore('chainge.curWallet')
                if(walletName === 'okx_bitcoin') {
                    await transferByOkx()
                } else {
                    await transferByUnisat()
                }
                dispatch(setCurInscription({}))
                dispatch(setRefreshData(refreshData + 1))
            } else {
                await transferByEvm()
            }

            
        } catch(error: any) {
            const errorStr = JSON.stringify(error)
            let errorMsg = ''
            if(errorStr.includes('shortMessage')) {
                errorMsg = error.shortMessage
            } else {
                errorMsg = 'Service is unavailable, please try again later.'
            }
            if(errorMsg) {
                toast.error(errorMsg, {
                    duration: 3000,
                    position: 'top-right',
                })
            }
        } finally {
            dispatch(setResetInfo())
        }
    }, [fromToken, fromChain, btnActive, btnStatus, dispatch, switchNetworkAsync, isBTC, refreshData, transferByOkx, transferByUnisat, transferByEvm])


    return { btnText, btnStatus, btnActive, fromChain, btnHandler }
}

export default useMainBtnBrc20